// useAidnFeatureGate
//
// Resolves AIDN feature state for the current org/account from the AIDN service
// (GET /aidn/v1/feature/status). AIDN's three featureConf flags (master/ui/api)
// were consolidated into the single AIDN_MASTER_ENABLED gate, so this hook reads
// `master` and surfaces `enabled` (= master) for the per-view splash logic
// (render the app iff `enabled`, else <AidnUnavailableNotice/>).
//
// Gating model (single flag):
//   - side-nav entry        : MASTER   (handled in the menu config / NavItem)
//   - /aidn app vs. splash  : MASTER   (this hook's `enabled`)
//
// The BE still returns ui/api mirrored to master as a back-compat shim; we ignore them.
// Fail-closed: any network/parse error => everything false.
//
// v2.0 item 6 (D.5): `noviceMode` mirrors AIDN_NOVICE_MODE_ENABLED. Unlike the create-method flags it is
// OPT-IN — absent or anything other than an explicit `true` resolves to false, so a BE that does not yet
// return the key leaves the design-mode selector hidden and the Designer on today's expert behaviour.
//
// Returns { master, enabled, noviceMode, loading, error, reason }.

import { useEffect, useState } from 'react';
import axiosInstance from 'api/axiosInstance';

// Legacy sentinel: an older BE returned 404 for "feature disabled".
const DISABLED_STATUS = 404;

// AIDN is a standalone service now (process.env.aidnHost); the feature ping must hit it, not the
// monolith — mirrors src/api/aidnService.js. Empty host => relative path (legacy embedded /aidn/v1).
const AIDN_HOST = (process.env.aidnHost || '').replace(/\/+$/, '');

// Feature-flag off (intentional): stay neutral — no "contact admin" (nothing for the user to escalate) and
// no hint that it's a feature flag.
const DISABLED_REASON = 'AI Defined Network is currently unavailable. Please try again later.';

export default function useAidnFeatureGate() {
  const [state, setState] = useState({
    master: null, // null = unknown (still loading)
    enabled: null,
    createText: null,
    createSpeech: null,
    createWizard: null,
    createEnvironment: null,
    noviceMode: false, // opt-in (D.5) — never null, so the selector is simply absent until the BE says yes
    loading: true,
    error: null,
    reason: null
  });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resp = await axiosInstance.get(`${AIDN_HOST}/aidn/v1/feature/status`, {
          validateStatus: (s) => s === 200 || s === DISABLED_STATUS
        });
        if (cancelled) return;

        // Legacy "disabled" sentinel.
        if (resp.status === DISABLED_STATUS) {
          setState({
            master: false,
            enabled: false,
            createText: false,
            createSpeech: false,
            createWizard: false,
            createEnvironment: false,
            noviceMode: false,
            loading: false,
            error: null,
            reason: DISABLED_REASON
          });
          return;
        }

        // 200: granular shape { master, createText, createSpeech, createWizard, createEnvironment }.
        // Back-compat: the old shape ({ enabled: true }) carries no `master` key => treat AIDN as enabled.
        const data = resp?.data || {};
        const granular = 'master' in data;
        const master = granular ? data.master === true : true;
        // /aidn app vs. splash is gated on the single MASTER flag (see the gating model above). Fail-closed:
        // master off (or absent in the granular shape) resolves to the splash.
        const enabled = master;
        // Create methods default ON unless explicitly disabled (the BE resolves them default-true; this also
        // tolerates an older agent that doesn't return these keys).
        const createText = data.createText !== false;
        const createSpeech = data.createSpeech !== false;
        const createWizard = data.createWizard !== false;
        const createEnvironment = data.createEnvironment !== false;
        // D.5: novice is OPT-IN, so it is the one flag that must be explicitly true (`!== false` would turn
        // it on for every BE that has not shipped the key yet — the opposite of "default OFF").
        const noviceMode = data.noviceMode === true;

        setState({
          master,
          enabled,
          createText,
          createSpeech,
          createWizard,
          createEnvironment,
          noviceMode,
          loading: false,
          error: null,
          reason: enabled ? null : DISABLED_REASON
        });
      } catch (err) {
        if (cancelled) return;
        // Fail-closed: treat as fully disabled.
        setState({
          master: false,
          enabled: false,
          createText: false,
          createSpeech: false,
          createWizard: false,
          createEnvironment: false,
          noviceMode: false,
          loading: false,
          error: err,
          reason: 'Please try again later'
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
